import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Card } from '../../components/ui/Card.js';
import { type Column, DataTable } from '../../components/ui/DataTable.js';
import { Select } from '../../components/ui/FormField.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { Spinner } from '../../components/ui/Spinner.js';
import { type DemandMiss, demandApi } from '../../services/demand.js';

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <Card className="flex flex-col gap-1">
      <p className="text-sm text-steel">{label}</p>
      <p className="text-3xl font-bold text-ink">{value}</p>
    </Card>
  );
}

const REASON_LABEL: Record<string, string> = {
  no_match: 'No match',
  slot_taken: 'Slot taken',
  car_blocked: 'Car blocked',
};

const columns: Column<DemandMiss>[] = [
  {
    key: 'created_at',
    header: 'When',
    render: (r) => new Date(r.created_at).toLocaleString('en-MY'),
  },
  { key: 'source', header: 'Source' },
  { key: 'reason', header: 'Reason', render: (r) => REASON_LABEL[r.reason] ?? r.reason },
  { key: 'location_id', header: 'Location' },
  {
    key: 'pickup_at',
    header: 'Window',
    render: (r) =>
      `${new Date(r.pickup_at).toLocaleDateString('en-MY')} – ${new Date(r.return_at).toLocaleDateString('en-MY')}`,
  },
  {
    key: 'was_authenticated',
    header: 'Signed in',
    render: (r) => (r.was_authenticated ? 'Yes' : 'No'),
  },
];

/** 214 (#310): "how much demand did we turn away" — a date-range summary plus a filterable,
 * paginated raw list. Admin-only (`read DemandMiss`). */
export function DemandMissPage() {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [locationId, setLocationId] = useState('');
  const [reason, setReason] = useState('');
  const [page, setPage] = useState(1);

  // biome-ignore lint/correctness/useExhaustiveDependencies: filter changes intentionally reset the page
  useEffect(() => {
    setPage(1);
  }, [from, to, locationId, reason]);

  const params = {
    from: from || undefined,
    to: to || undefined,
    location_id: locationId || undefined,
    reason: reason || undefined,
  };

  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ['demand-miss-summary', from, to],
    queryFn: () => demandApi.summary({ from: params.from, to: params.to }),
    retry: false,
  });

  const {
    data: list,
    isLoading: listLoading,
    error,
  } = useQuery({
    queryKey: ['demand-miss-list', from, to, locationId, reason, page],
    queryFn: () => demandApi.list({ ...params, page, limit: 50 }),
    retry: false,
  });

  return (
    <div>
      <PageHeader
        title="Turned-Away Demand"
        description="Booking attempts that failed for lack of availability"
      />

      <Card className="mb-4 flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-sm" htmlFor="demand-from">
          <span className="text-steel">From</span>
          <input
            id="demand-from"
            type="date"
            className="rounded-md border border-hairline bg-canvas px-3 py-2 text-sm"
            value={from}
            onChange={(e) => setFrom((e.target as HTMLInputElement).value)}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm" htmlFor="demand-to">
          <span className="text-steel">To</span>
          <input
            id="demand-to"
            type="date"
            className="rounded-md border border-hairline bg-canvas px-3 py-2 text-sm"
            value={to}
            onChange={(e) => setTo((e.target as HTMLInputElement).value)}
          />
        </label>
        <Select
          aria-label="Reason"
          value={reason}
          onChange={(e) => setReason((e.target as HTMLSelectElement).value)}
        >
          <option value="">Any reason</option>
          <option value="no_match">No match</option>
          <option value="slot_taken">Slot taken</option>
          <option value="car_blocked">Car blocked</option>
        </Select>
      </Card>

      {summaryLoading ? (
        <div className="flex justify-center py-8">
          <Spinner size="lg" />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
          <StatCard label="Total attempts" value={summary?.total ?? 0} />
          <Card>
            <p className="text-sm text-steel mb-2">Top locations</p>
            <ul className="space-y-1">
              {(summary?.by_location ?? []).slice(0, 5).map((row) => (
                <li key={row.location_id}>
                  <button
                    type="button"
                    className="flex w-full justify-between text-sm"
                    onClick={() => setLocationId(row.location_id)}
                  >
                    <span>{row.name ?? row.location_id}</span>
                    <span className="text-steel">{row.count}</span>
                  </button>
                </li>
              ))}
              {(summary?.by_location ?? []).length === 0 && (
                <li className="text-sm text-steel">No attempts recorded</li>
              )}
            </ul>
          </Card>
          <Card>
            <p className="text-sm text-steel mb-2">Top pickup dates</p>
            <ul className="space-y-1">
              {(summary?.by_date ?? []).slice(0, 5).map((row) => (
                <li key={row.date} className="flex justify-between text-sm">
                  <span>{row.date}</span>
                  <span className="text-steel">{row.count}</span>
                </li>
              ))}
              {(summary?.by_date ?? []).length === 0 && (
                <li className="text-sm text-steel">No attempts recorded</li>
              )}
            </ul>
          </Card>
        </div>
      )}

      <DataTable
        columns={columns}
        data={list?.items ?? []}
        loading={listLoading}
        keyExtractor={(r) => r._id}
        error={error as { status?: number } | null}
        pagination={{
          page,
          totalPages: list?.pages ?? 1,
          onPageChange: setPage,
        }}
      />
    </div>
  );
}
